import type { MerchantSpec } from "@cpos/shared/src/dev-merchant/spec";
import { rollBusinessName } from "@cpos/shared/src/dev-merchant/generators";
import type { CreateMerchantBloc } from "@/application/tools/create_merchant_bloc";
import { SectionCard, FormField } from "./shared";
import { inputClass } from "./styles";

interface Props {
  draft: MerchantSpec;
  bloc: CreateMerchantBloc;
  disabled?: boolean;
}

function slugify(name: string): string {
  return name.replace(/[^A-Za-z0-9]/g, "");
}

export function BusinessSection({ draft, bloc, disabled }: Props) {
  const { business } = draft;

  function patch(next: Partial<typeof business>) {
    bloc.setDraft({ business: { ...business, ...next } });
  }

  return (
    <SectionCard
      title="Business"
      onRoll={() => patch({ name: rollBusinessName() })}
      rollDisabled={disabled}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Name">
          <input
            className={inputClass}
            value={business.name}
            onChange={(e) => patch({ name: e.target.value })}
            disabled={disabled}
          />
        </FormField>

        <FormField
          label="Slug"
          hint={
            business.slug
              ? undefined
              : `Derived from name: ${slugify(business.name) || "—"}`
          }
        >
          <input
            className={inputClass}
            value={business.slug ?? ""}
            placeholder={slugify(business.name)}
            onChange={(e) => patch({ slug: e.target.value || undefined })}
            disabled={disabled}
          />
        </FormField>

        <FormField label="Type">
          <select
            className={inputClass}
            value={business.type}
            onChange={(e) =>
              patch({ type: e.target.value as "individual" | "company" })
            }
            disabled={disabled}
          >
            <option value="individual">individual</option>
            <option value="company">company</option>
          </select>
        </FormField>

        <FormField label="Country">
          <select
            className={inputClass}
            value={business.country}
            onChange={(e) => patch({ country: e.target.value as "CA" | "US" })}
            disabled={disabled}
          >
            <option value="CA">CA</option>
            <option value="US">US</option>
          </select>
        </FormField>

        <FormField label="MCC">
          <input
            className={inputClass}
            value={business.mcc}
            onChange={(e) => patch({ mcc: e.target.value })}
            disabled={disabled}
          />
        </FormField>

        <FormField label="Onboarding status">
          <select
            className={inputClass}
            value={business.onboardingStatus}
            onChange={(e) =>
              patch({
                onboardingStatus: e.target.value as
                  "completed" | "in_progress" | "incomplete",
              })
            }
            disabled={disabled}
          >
            <option value="completed">completed</option>
            <option value="in_progress">in_progress</option>
            <option value="incomplete">incomplete</option>
          </select>
        </FormField>

        <FormField label="Subscription status">
          <select
            className={inputClass}
            value={business.subscriptionStatus}
            onChange={(e) =>
              patch({
                subscriptionStatus: e.target.value as
                  "active" | "trialing" | "past_due" | "inactive",
              })
            }
            disabled={disabled}
          >
            <option value="active">active</option>
            <option value="trialing">trialing</option>
            <option value="past_due">past_due</option>
            <option value="inactive">inactive</option>
          </select>
        </FormField>

        <FormField label="Identity verification status">
          <select
            className={inputClass}
            value={business.identityVerificationStatus ?? ""}
            onChange={(e) =>
              patch({
                identityVerificationStatus:
                  (e.target.value as "verified" | "pending" | "") || undefined,
              })
            }
            disabled={disabled}
          >
            <option value="">unset</option>
            <option value="verified">verified</option>
            <option value="pending">pending</option>
          </select>
        </FormField>

        <FormField label="Payout interval">
          <select
            className={inputClass}
            value={business.payoutInterval ?? ""}
            onChange={(e) =>
              patch({
                payoutInterval:
                  (e.target.value as
                    "manual" | "daily" | "weekly" | "monthly" | "") ||
                  undefined,
              })
            }
            disabled={disabled}
          >
            <option value="">unset</option>
            <option value="manual">manual</option>
            <option value="daily">daily</option>
            <option value="weekly">weekly</option>
            <option value="monthly">monthly</option>
          </select>
        </FormField>

        <FormField label="Transaction limit (cents)">
          <input
            type="number"
            className={inputClass}
            value={business.transactionLimitCents ?? ""}
            onChange={(e) =>
              patch({
                transactionLimitCents: e.target.value
                  ? Number(e.target.value)
                  : undefined,
              })
            }
            disabled={disabled}
          />
        </FormField>

        <FormField
          label="Website URL"
          hint="Stripe business_profile.url; defaults to the seed URL"
          className="sm:col-span-2"
        >
          <input
            className={inputClass}
            value={business.url ?? ""}
            placeholder="https://seed-merchant.cpos-seed.test"
            onChange={(e) => patch({ url: e.target.value || undefined })}
            disabled={disabled}
          />
        </FormField>

        <FormField label="Product description" className="sm:col-span-2">
          <input
            className={inputClass}
            value={business.productDescription}
            onChange={(e) => patch({ productDescription: e.target.value })}
            disabled={disabled}
          />
        </FormField>
      </div>
    </SectionCard>
  );
}
